import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { INVOICE_STATUSES, INVOICE_STATUS_COLORS, PROJECT_TYPES, CATEGORIES } from '@/lib/constants';
import { formatCurrency, calculateWeeks, generateMonthlyInvoices, getLastFridayOfMonth } from '@/lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Save, DollarSign, FileText, CalendarIcon, Layers, Plus, Trash2, Filter, Download } from 'lucide-react';
import { format, parseISO, addMonths } from 'date-fns';
import { MultiSelectFilter } from '@/components/MultiSelectFilter';
import * as XLSX from 'xlsx';

const INVOICE_ELIGIBLE = ['sow', 'execute'];

export default function ConsolidatedInvoices() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [adhocInvoices, setAdhocInvoices] = useState([]);
  const [activeMonth, setActiveMonth] = useState(null);
  const [editingDateIdx, setEditingDateIdx] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedClients, setSelectedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEntry, setNewEntry] = useState({
    projectId: '', clientName: '', projectName: '', projectType: 'staff_aug', category: 'regular',
    poNumber: '', invoiceValue: 0, consumptionAmount: 0, invoiceNumber: '', invoiceDate: '', invoiceDateFormatted: '', status: 'po_awaited',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [projs, adhocs] = await Promise.all([api.getProjects(), api.getAdhocInvoices()]);
      setProjects(projs);
      setAdhocInvoices(adhocs);

      const invoices = [];
      projs.forEach(p => {
        if (!p.startDate || !p.endDate) return;
        if (!INVOICE_ELIGIBLE.includes(p.status)) return;
        const numWeeks = calculateWeeks(p.startDate, p.endDate);
        const generated = generateMonthlyInvoices(p.startDate, p.endDate, p.resources || [], numWeeks);

        const merged = generated.map(gen => {
          const saved = (p.invoices || []).find(inv => inv.monthKey === gen.monthKey);
          if (saved) {
            return {
              ...gen,
              invoiceValue: saved.invoiceValue !== undefined ? saved.invoiceValue : gen.invoiceValue,
              consumptionAmount: saved.consumptionAmount !== undefined ? saved.consumptionAmount : gen.consumptionAmount,
              invoiceNumber: saved.invoiceNumber || gen.invoiceNumber || '',
              status: saved.status || gen.status,
              invoiceDate: saved.invoiceDate || gen.invoiceDate,
              invoiceDateFormatted: saved.invoiceDateFormatted || gen.invoiceDateFormatted,
            };
          }
          return gen;
        });

        merged.forEach(inv => {
          invoices.push({
            ...inv,
            projectId: p.id,
            projectName: p.projectName || 'Untitled',
            clientName: p.clientName || 'No client',
            projectType: p.projectType || '',
            category: p.category || '',
            poNumber: p.poNumber || '',
          });
        });
      });

      // Add ad-hoc invoices
      adhocs.forEach(inv => {
        invoices.push({ ...inv, isAdhoc: true });
      });

      setAllInvoices(invoices);
      setLoading(false);
    } catch {
      setProjects([]);
      setAllInvoices([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const months = useMemo(() => {
    const unique = [...new Set(allInvoices.map(inv => inv.monthKey))].sort();
    return unique.map(key => {
      const inv = allInvoices.find(i => i.monthKey === key);
      return { key, label: inv?.month || key };
    });
  }, [allInvoices]);

  // Unique client list from all invoices
  const clients = useMemo(() => {
    const names = [...new Set(allInvoices.map(inv => inv.clientName).filter(Boolean))].sort();
    return names.map(n => ({ value: n, label: n }));
  }, [allInvoices]);

  useEffect(() => {
    if (months.length > 0 && !activeMonth) {
      setActiveMonth(months[0].key);
    }
  }, [months, activeMonth]);

  const filteredInvoices = useMemo(() => {
    if (!activeMonth) return [];
    return allInvoices.filter(inv => {
      if (inv.isPlaceholder) return false;
      if (inv.monthKey !== activeMonth) return false;
      if (selectedClients.length > 0 && !selectedClients.includes(inv.clientName)) return false;
      return true;
    });
  }, [allInvoices, activeMonth, selectedClients]);

  const monthInvoiceTotal = useMemo(() => {
    return filteredInvoices.reduce((sum, inv) => sum + (inv.invoiceValue || 0), 0);
  }, [filteredInvoices]);

  const monthConsumptionTotal = useMemo(() => {
    return filteredInvoices.reduce((sum, inv) => sum + (inv.consumptionAmount ?? inv.invoiceValue ?? 0), 0);
  }, [filteredInvoices]);

  // Unique project count for the active month's filtered invoices
  const monthProjectCount = useMemo(() => {
    const ids = new Set();
    filteredInvoices.forEach(inv => {
      if (inv.projectId) ids.add(inv.projectId);
    });
    return ids.size;
  }, [filteredInvoices]);

  const findRealIdx = (invoiceIdx) => {
    let count = -1;
    for (let i = 0; i < allInvoices.length; i++) {
      if (allInvoices[i].monthKey !== activeMonth) continue;
      if (selectedClients.length > 0 && !selectedClients.includes(allInvoices[i].clientName)) continue;
      count++;
      if (count === invoiceIdx) return i;
    }
    return -1;
  };

  const updateInvoiceField = (invoiceIdx, field, value) => {
    setAllInvoices(prev => {
      const updated = [...prev];
      const realIdx = findRealIdx(invoiceIdx);
      if (realIdx >= 0) {
        updated[realIdx] = { ...updated[realIdx], [field]: value };
      }
      return updated;
    });
  };

  const updateInvoiceDate = (invoiceIdx, date) => {
    if (!date) return;
    const formatted = format(date, 'yyyy-MM-dd');
    const display = format(date, 'MMM d, yyyy');
    setAllInvoices(prev => {
      const updated = [...prev];
      const realIdx = findRealIdx(invoiceIdx);
      if (realIdx >= 0) {
        updated[realIdx] = { ...updated[realIdx], invoiceDate: formatted, invoiceDateFormatted: display };
      }
      return updated;
    });
    setEditingDateIdx(null);
  };

  const handleAddAdhocEntry = async () => {
    if (!activeMonth) return;
    if (!newEntry.projectId) {
      toast.error('Please select a project first.');
      return;
    }
    // Check remaining balance before allowing add
    const balances = getProjectBalances(newEntry.projectId);
    if (newEntry.invoiceValue > 0 && balances.remainingInvoice <= 0) {
      toast.error(`Invoice remaining balance is ${formatCurrency(balances.remainingInvoice)}. Please update existing invoice amounts first to create remaining balance.`);
      return;
    }
    if (newEntry.invoiceValue > balances.remainingInvoice && balances.remainingInvoice > 0) {
      toast.error(`Invoice amount (${formatCurrency(newEntry.invoiceValue)}) exceeds remaining invoice balance (${formatCurrency(balances.remainingInvoice)}). Please reduce the amount.`);
      return;
    }
    if (newEntry.consumptionAmount > 0 && balances.remainingConsumption <= 0) {
      toast.error(`Consumption remaining balance is ${formatCurrency(balances.remainingConsumption)}. Please update existing consumption amounts first to create remaining balance.`);
      return;
    }
    if (newEntry.consumptionAmount > balances.remainingConsumption && balances.remainingConsumption > 0) {
      toast.error(`Consumption amount (${formatCurrency(newEntry.consumptionAmount)}) exceeds remaining consumption balance (${formatCurrency(balances.remainingConsumption)}). Please reduce the amount.`);
      return;
    }

    const monthLabel = months.find(m => m.key === activeMonth)?.label || activeMonth;
    const [year, month] = activeMonth.split('-').map(Number);
    const lastFri = getLastFridayOfMonth(year, month - 1);
    const entry = {
      ...newEntry,
      monthKey: activeMonth,
      month: monthLabel,
      invoiceDate: newEntry.invoiceDate || format(lastFri, 'yyyy-MM-dd'),
      invoiceDateFormatted: newEntry.invoiceDateFormatted || format(lastFri, 'MMM d, yyyy'),
      consumptionAmount: newEntry.consumptionAmount,
      isAdhoc: true,
    };
    try {
      await api.createAdhocInvoice(entry);
      toast.success('Ad-hoc invoice added');
      setShowAddDialog(false);
      setNewEntry({ projectId: '', clientName: '', projectName: '', projectType: 'staff_aug', category: 'regular', poNumber: '', invoiceValue: 0, consumptionAmount: 0, invoiceNumber: '', invoiceDate: '', invoiceDateFormatted: '', status: 'po_awaited' });
      await loadData();
      // After adding, recalculate balance from fresh data
      const [freshProjs, freshAdhocs] = await Promise.all([api.getProjects(), api.getAdhocInvoices()]);
      const proj = freshProjs.find(p => p.id === entry.projectId);
      if (proj) {
        const te = (proj.resources || []).reduce((sum, res) => sum + (res.weeklyAllocation || []).reduce((s, v) => s + (v || 0), 0) * (res.rate || 0), 0);
        let tInv = 0, tCon = 0;
        // Regular invoices
        if (proj.startDate && proj.endDate) {
          const nw = calculateWeeks(proj.startDate, proj.endDate);
          const gen = generateMonthlyInvoices(proj.startDate, proj.endDate, proj.resources || [], nw);
          gen.forEach(g => {
            const saved = (proj.invoices || []).find(si => si.monthKey === g.monthKey);
            tInv += saved?.invoiceValue !== undefined ? saved.invoiceValue : g.invoiceValue;
            tCon += saved?.consumptionAmount !== undefined ? saved.consumptionAmount : g.consumptionAmount;
          });
        }
        // Ad-hoc invoices for this project
        freshAdhocs.forEach(a => {
          if (a.projectId === proj.id || a.projectName === proj.projectName) {
            tInv += (a.invoiceValue || 0);
            tCon += (a.consumptionAmount ?? a.invoiceValue ?? 0);
          }
        });
        const remInv = Math.round(te - tInv);
        const remCon = Math.round(te - tCon);
        if (remInv < 0 || remCon < 0) {
          toast.warning(`Remaining balance is now negative. Invoice: ${formatCurrency(remInv)}, Consumption: ${formatCurrency(remCon)}. Please adjust invoices so balance becomes zero.`, { duration: 8000 });
        }
      }
    } catch {
      toast.error('Failed to add entry');
    }
  };

  const handleDeleteAdhoc = async (inv) => {
    try {
      await api.deleteAdhocInvoice(inv.id);
      toast.success('Ad-hoc invoice deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSaveAll = async () => {
    try {
      const projectUpdates = {};
      const adhocUpdates = [];

      allInvoices.forEach(inv => {
        if (inv.isAdhoc) {
          adhocUpdates.push(inv);
        } else {
          if (!projectUpdates[inv.projectId]) projectUpdates[inv.projectId] = [];
          projectUpdates[inv.projectId].push({
            monthKey: inv.monthKey, month: inv.month, invoiceValue: inv.invoiceValue,
            consumptionAmount: inv.consumptionAmount, invoiceDate: inv.invoiceDate,
            invoiceDateFormatted: inv.invoiceDateFormatted, totalEstimate: inv.totalEstimate,
            remainingValue: inv.remainingValue, invoiceNumber: inv.invoiceNumber || '', status: inv.status,
          });
        }
      });

      const promises = [];
      projects.forEach(p => {
        if (projectUpdates[p.id]) {
          promises.push(api.updateProject(p.id, { ...p, invoices: projectUpdates[p.id] }));
        }
      });
      adhocUpdates.forEach(inv => {
        promises.push(api.updateAdhocInvoice(inv.id, inv));
      });

      await Promise.all(promises);
      toast.success('All invoices saved successfully');
      loadData();
    } catch {
      toast.error('Failed to save invoices');
    }
  };

  const getTypeLabel = (val) => PROJECT_TYPES.find(t => t.value === val)?.label || val || '-';
  const getCategoryLabel = (val) => CATEGORIES.find(c => c.value === val)?.label || val || '-';

  // Calculate remaining balances per project (for ad-hoc validation)
  const getProjectBalances = (projectId) => {
    const proj = projects.find(p => p.id === projectId);
    if (!proj) return { totalEstimate: 0, remainingInvoice: 0, remainingConsumption: 0 };
    const totalEstimate = (proj.resources || []).reduce((sum, res) => {
      return sum + (res.weeklyAllocation || []).reduce((s, v) => s + (v || 0), 0) * (res.rate || 0);
    }, 0);
    // Sum all invoiced/consumed across regular invoices and ad-hoc for this project
    let totalInvoiced = 0;
    let totalConsumed = 0;
    allInvoices.forEach(inv => {
      if (inv.projectId === projectId || (inv.isAdhoc && inv.projectName === proj.projectName)) {
        totalInvoiced += (inv.invoiceValue || 0);
        totalConsumed += (inv.consumptionAmount ?? inv.invoiceValue ?? 0);
      }
    });
    return {
      totalEstimate: Math.round(totalEstimate),
      remainingInvoice: Math.round(totalEstimate - totalInvoiced),
      remainingConsumption: Math.round(totalEstimate - totalConsumed),
    };
  };

  const handleExportExcel = () => {
    if (filteredInvoices.length === 0) return;
    const data = filteredInvoices.map(inv => ({
      'Client Name': inv.clientName,
      'Project Type': getTypeLabel(inv.projectType),
      'Category': getCategoryLabel(inv.category),
      'PO Number': inv.poNumber || '',
      'Project Name': inv.projectName,
      'Invoice #': inv.invoiceNumber || '',
      'Invoice Date': inv.invoiceDateFormatted || '',
      'Invoice Amount ($)': inv.invoiceValue || 0,
      'Consumption Amt ($)': inv.consumptionAmount ?? inv.invoiceValue ?? 0,
      'Status': INVOICE_STATUSES.find(s => s.value === inv.status)?.label || inv.status,
    }));
    const monthLabel = months.find(m => m.key === activeMonth)?.label || activeMonth;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, monthLabel);
    XLSX.writeFile(wb, `consolidated_invoices_${activeMonth}.xlsx`);
    toast.success('Invoice table exported to Excel');
  };

  return (
    <div data-testid="consolidated-invoices-page">
      <Toaster theme="dark" />

      {loading ? (
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-4 w-96" />
            <Skeleton className="h-3 w-80" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
          <Skeleton className="h-96 rounded-lg" />
        </div>
      ) : (
        <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-manrope font-bold text-3xl sm:text-4xl tracking-tight text-white" data-testid="consolidated-title">
            Invoices
          </h1>
          <p className="text-zinc-500 text-sm mt-1 font-manrope">
            Consolidated view across all projects
          </p>
          <p className="text-amber-400/80 text-xs mt-1 font-manrope flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400/80"></span>
            Only projects with status SoW or Execute are considered
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          <MultiSelectFilter
            label="Clients"
            options={clients}
            selected={selectedClients}
            onSelectionChange={setSelectedClients}
            testId="invoices-client-filter"
          />
          <Button
            data-testid="add-adhoc-invoice-btn"
            onClick={() => setShowAddDialog(true)}
            className="bg-zinc-900 text-zinc-100 border border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700 font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
          <Button
            data-testid="save-all-invoices-btn"
            onClick={handleSaveAll}
            className="bg-brand-500 text-white hover:bg-brand-400 hover:shadow-[0_0_20px_-5px_rgba(168,0,151,0.5)] font-semibold active:scale-95 transition-all duration-200"
          >
            <Save className="w-4 h-4 mr-2" />
            Save All
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
              <Layers className="w-5 h-5 text-brand-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Projects This Month</p>
              <p className="font-mono text-lg font-bold text-white" data-testid="total-projects-count">{monthProjectCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">This Month Invoice</p>
              <p className="font-mono text-lg font-bold text-white" data-testid="month-invoice-total">{formatCurrency(monthInvoiceTotal)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">This Month Consumption</p>
              <p className="font-mono text-lg font-bold text-white" data-testid="month-consumption-total">{formatCurrency(monthConsumptionTotal)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {allInvoices.length === 0 ? (
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-12 text-center" data-testid="no-invoices-state">
            <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-zinc-600" />
            </div>
            <h2 className="font-manrope font-bold text-xl text-zinc-300 mb-2">No invoices yet</h2>
            <p className="text-zinc-500 text-sm max-w-sm mx-auto mb-6">
              Create project estimates with dates and resource allocations to generate invoices, or add ad-hoc entries.
            </p>
            <Button
              onClick={() => navigate('/estimate/new')}
              data-testid="create-estimate-from-invoices-btn"
              className="bg-brand-500 text-white hover:bg-brand-400 font-semibold"
            >
              Create Estimate
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2" data-testid="month-tabs">
            {months.map(m => (
              <button
                key={m.key}
                data-testid={`month-tab-${m.key}`}
                onClick={() => setActiveMonth(m.key)}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeMonth === m.key
                    ? 'bg-brand-500 text-white font-bold'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800 border border-zinc-800'
                }`}
              >
                {m.label}
              </button>
            ))}
            <button
              data-testid="add-month-tab-btn"
              onClick={() => {
                // Add next month after the last existing month
                let nextKey;
                if (months.length > 0) {
                  const lastKey = months[months.length - 1].key;
                  const [y, m] = lastKey.split('-').map(Number);
                  const nextDate = addMonths(new Date(y, m - 1, 1), 1);
                  nextKey = format(nextDate, 'yyyy-MM');
                } else {
                  nextKey = format(new Date(), 'yyyy-MM');
                }
                // Check if month already exists
                if (months.find(m => m.key === nextKey)) {
                  toast.info('Next month tab already exists.');
                  return;
                }
                const label = format(parseISO(nextKey + '-01'), 'MMMM yyyy');
                // Add a placeholder entry so the tab shows up
                setAllInvoices(prev => [...prev, {
                  monthKey: nextKey,
                  month: label,
                  invoiceValue: 0,
                  consumptionAmount: 0,
                  totalEstimate: 0,
                  status: 'po_awaited',
                  isPlaceholder: true,
                  projectId: '__placeholder__',
                  clientName: '',
                  projectName: '',
                }]);
                setActiveMonth(nextKey);
                toast.success(`Added ${label} tab`);
              }}
              className="px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 bg-zinc-900 text-zinc-500 hover:text-brand-400 hover:bg-zinc-800 border border-dashed border-zinc-700"
              title="Add next month"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <Card className="bg-zinc-950 border-zinc-900" data-testid="consolidated-invoice-table">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-manrope font-bold text-base text-white">
                  {months.find(m => m.key === activeMonth)?.label || 'Invoices'}
                </CardTitle>
                <Badge className="bg-brand-500/10 text-brand-400 border border-brand-500/20 font-mono text-xs rounded-full">
                  {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[130px]">Client Name</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[130px]">Project Type</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[100px]">Category</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[100px]">PO Number</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[150px]">Project Name</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[120px]">Invoice #</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[160px]">Invoice Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[140px]">Invoice Amount</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[140px]">Consumption Amt</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope min-w-[160px]">Status</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-zinc-500 py-8">
                          No invoices for this month
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInvoices.map((inv, idx) => {
                        const statusColor = INVOICE_STATUS_COLORS[inv.status] || INVOICE_STATUS_COLORS.po_awaited;
                        return (
                          <TableRow key={`${inv.projectId || inv.id}-${idx}`} className="border-zinc-900 hover:bg-zinc-900/30" data-testid={`consolidated-row-${idx}`}>
                            <TableCell className="font-manrope text-sm text-zinc-300" data-testid={`consolidated-client-${idx}`}>
                              {inv.clientName}
                            </TableCell>
                            <TableCell className="font-manrope text-xs text-zinc-400" data-testid={`consolidated-type-${idx}`}>
                              {getTypeLabel(inv.projectType)}
                            </TableCell>
                            <TableCell className="font-manrope text-xs text-zinc-400" data-testid={`consolidated-category-${idx}`}>
                              {getCategoryLabel(inv.category)}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-zinc-400" data-testid={`consolidated-po-${idx}`}>
                              {inv.poNumber || '-'}
                            </TableCell>
                            <TableCell data-testid={`consolidated-project-${idx}`}>
                              {inv.isAdhoc && !inv.projectId ? (
                                <span className="font-manrope text-sm text-zinc-300">{inv.projectName}</span>
                              ) : (
                                <button
                                  onClick={() => navigate(`/invoices/${inv.projectId}`)}
                                  className="font-manrope text-sm text-brand-400 hover:text-brand-300 hover:underline transition-colors text-left"
                                  data-testid={`consolidated-project-link-${idx}`}
                                >
                                  {inv.projectName}
                                </button>
                              )}
                            </TableCell>
                            <TableCell className="p-2">
                              <Input
                                data-testid={`consolidated-invoice-number-${idx}`}
                                value={inv.invoiceNumber || ''}
                                onChange={e => updateInvoiceField(idx, 'invoiceNumber', e.target.value)}
                                placeholder="INV-..."
                                className="bg-zinc-950 border-zinc-800 font-mono text-xs h-8 w-24 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 placeholder:text-zinc-700"
                              />
                            </TableCell>
                            <TableCell className="p-2">
                              <Popover open={editingDateIdx === idx} onOpenChange={(open) => setEditingDateIdx(open ? idx : null)}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    data-testid={`consolidated-date-btn-${idx}`}
                                    className="w-full justify-start text-left font-mono text-xs bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700 h-8 text-zinc-300"
                                  >
                                    <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-zinc-500" />
                                    {inv.invoiceDateFormatted || 'Set date'}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={inv.invoiceDate ? parseISO(inv.invoiceDate) : undefined}
                                    onSelect={(date) => updateInvoiceDate(idx, date)}
                                    className="text-white"
                                  />
                                </PopoverContent>
                              </Popover>
                            </TableCell>
                            <TableCell className="p-2">
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-mono">$</span>
                                <Input
                                  type="number"
                                  data-testid={`consolidated-amount-input-${idx}`}
                                  value={inv.invoiceValue}
                                  onChange={e => updateInvoiceField(idx, 'invoiceValue', Number(e.target.value) || 0)}
                                  className="bg-zinc-950 border-zinc-800 font-mono text-sm h-8 pl-5 w-28 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="p-2">
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-mono">$</span>
                                <Input
                                  type="number"
                                  data-testid={`consolidated-consumption-input-${idx}`}
                                  value={inv.consumptionAmount ?? inv.invoiceValue}
                                  onChange={e => updateInvoiceField(idx, 'consumptionAmount', Number(e.target.value) || 0)}
                                  className="bg-zinc-950 border-zinc-800 font-mono text-sm h-8 pl-5 w-28 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="p-2">
                              <Select value={inv.status} onValueChange={(val) => updateInvoiceField(idx, 'status', val)}>
                                <SelectTrigger
                                  data-testid={`consolidated-status-select-${idx}`}
                                  className={`bg-transparent border h-8 text-xs w-36 ${statusColor}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-950 border-zinc-800">
                                  {INVOICE_STATUSES.map(s => (
                                    <SelectItem key={s.value} value={s.value} className="text-zinc-200 text-xs focus:bg-zinc-900 focus:text-white">
                                      {s.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="p-1">
                              {inv.isAdhoc && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAdhoc(inv)}
                                  data-testid={`delete-adhoc-${idx}`}
                                  className="text-zinc-600 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
            {/* Export button at bottom */}
            <div className="flex justify-end p-4 border-t border-zinc-900">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExportExcel}
                data-testid="export-invoices-btn"
                className="text-zinc-400 hover:text-white hover:bg-zinc-900 font-medium"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </Card>
        </>
      )}

      {/* Ad-hoc Invoice Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-manrope text-lg text-white">Add Ad-hoc Invoice Entry</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Select Project</Label>
              <Select
                value={newEntry.projectId || ''}
                onValueChange={v => {
                  const proj = projects.find(p => p.id === v);
                  if (proj) {
                    setNewEntry(p => ({
                      ...p,
                      projectId: proj.id,
                      clientName: proj.clientName || '',
                      projectName: proj.projectName || '',
                      projectType: proj.projectType || 'staff_aug',
                      category: proj.category || 'regular',
                      poNumber: proj.poNumber || '',
                    }));
                  }
                }}
              >
                <SelectTrigger data-testid="adhoc-project-select" className="bg-zinc-950 border-zinc-800 text-sm">
                  <SelectValue placeholder="Choose a project..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-zinc-200 text-xs focus:bg-zinc-900">
                      {p.id} — {p.projectName || 'Untitled'} ({p.clientName || 'No client'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newEntry.projectId && (() => {
              const bal = getProjectBalances(newEntry.projectId);
              return (
                <div className="col-span-2 flex gap-4 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Total Estimate</p>
                    <p className="font-mono text-sm text-white">{formatCurrency(bal.totalEstimate)}</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Rem. Invoice</p>
                    <p className={`font-mono text-sm ${bal.remainingInvoice <= 0 ? 'text-red-400' : 'text-emerald-400'}`}>{formatCurrency(bal.remainingInvoice)}</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Rem. Consumption</p>
                    <p className={`font-mono text-sm ${bal.remainingConsumption <= 0 ? 'text-red-400' : 'text-emerald-400'}`}>{formatCurrency(bal.remainingConsumption)}</p>
                  </div>
                </div>
              );
            })()}
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Client Name</Label>
              <Input data-testid="adhoc-client-input" value={newEntry.clientName} readOnly className="bg-zinc-900/50 border-zinc-800 text-sm text-zinc-400 cursor-not-allowed" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Project Name</Label>
              <Input data-testid="adhoc-project-input" value={newEntry.projectName} readOnly className="bg-zinc-900/50 border-zinc-800 text-sm text-zinc-400 cursor-not-allowed" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Project Type</Label>
              <Select value={newEntry.projectType} onValueChange={v => setNewEntry(p => ({ ...p, projectType: v }))}>
                <SelectTrigger data-testid="adhoc-type-select" className="bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-zinc-200 text-xs focus:bg-zinc-900">{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Category</Label>
              <Select value={newEntry.category} onValueChange={v => setNewEntry(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="adhoc-category-select" className="bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value} className="text-zinc-200 text-xs focus:bg-zinc-900">{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">PO Number</Label>
              <Input data-testid="adhoc-po-input" value={newEntry.poNumber} onChange={e => setNewEntry(p => ({ ...p, poNumber: e.target.value }))} placeholder="Optional" className="bg-zinc-950 border-zinc-800 text-sm focus:border-brand-500" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Invoice #</Label>
              <Input data-testid="adhoc-invoice-number-input" value={newEntry.invoiceNumber} onChange={e => setNewEntry(p => ({ ...p, invoiceNumber: e.target.value }))} placeholder="INV-..." className="bg-zinc-950 border-zinc-800 font-mono text-sm focus:border-brand-500" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Invoice Amount ($)</Label>
              <Input data-testid="adhoc-amount-input" type="number" value={newEntry.invoiceValue} onChange={e => setNewEntry(p => ({ ...p, invoiceValue: Number(e.target.value) || 0 }))} className="bg-zinc-950 border-zinc-800 font-mono text-sm focus:border-brand-500" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Consumption Amt ($)</Label>
              <Input data-testid="adhoc-consumption-input" type="number" value={newEntry.consumptionAmount} onChange={e => setNewEntry(p => ({ ...p, consumptionAmount: Number(e.target.value) || 0 }))} className="bg-zinc-950 border-zinc-800 font-mono text-sm focus:border-brand-500" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-1 block">Status</Label>
              <Select value={newEntry.status} onValueChange={v => setNewEntry(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="adhoc-status-select" className="bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {INVOICE_STATUSES.map(s => <SelectItem key={s.value} value={s.value} className="text-zinc-200 text-xs focus:bg-zinc-900">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)} className="text-zinc-400 hover:text-white">Cancel</Button>
            <Button data-testid="adhoc-submit-btn" onClick={handleAddAdhocEntry} className="bg-brand-500 text-white hover:bg-brand-400 font-semibold">Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}
